import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { X, Coins, UserCheck, UserX } from 'lucide-react';
import { safeFormat as format } from '../../utils/safeFormat';
import { Button } from '../common/Button';
import { User, GTWallet } from '../../types';

interface UserProfileModalProps {
  user: User;
  wallet: GTWallet | undefined;
  onClose: () => void;
  onAdjustGT: () => void;
  onBan: () => void;
  onUnban: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  user,
  wallet,
  onClose,
  onAdjustGT,
  onBan,
  onUnban
}) => {
  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-20 bg-gradient-to-r from-primary to-primary/70">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-10"
          >
            <X size={18} className="text-white" />
          </button>
        </div>

        {/* Profile Info */}
        <div className="px-6 pb-6">
          <div className="flex items-end gap-4 -mt-12 mb-4 relative z-10">
            <img
              src={user.photoURL}
              alt={user.username}
              className="w-20 h-20 rounded-xl border-4 border-white dark:border-surface-dark object-cover shadow-lg bg-white dark:bg-surface-dark"
            />
            <div className="pb-1">
              <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {user.displayName}
              </h3>
              <p className="text-text-secondary-light dark:text-text-secondary-dark">
                @{user.username}
              </p>
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex gap-2 mb-4">
            {user.isAdmin && <span className="badge-primary text-xs">Admin</span>}
            {user.isBanned && <span className="badge-error text-xs">Banned</span>}
            {!user.isAdmin && !user.isBanned && <span className="badge-success text-xs">Active</span>}
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Email</p>
              <p className="text-sm font-medium truncate">{user.email}</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">GT Balance</p>
              <p className="text-sm font-bold text-primary">{wallet?.balance.toLocaleString() ?? 0} GT</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Total Earned</p>
              <p className="text-sm font-medium">{wallet?.totalEarned.toLocaleString() ?? 0} GT</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Total Spent</p>
              <p className="text-sm font-medium">{wallet?.totalSpent.toLocaleString() ?? 0} GT</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Joined</p>
              <p className="text-sm font-medium">{format(user.createdAt, 'MMM d, yyyy')}</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Provider</p>
              <p className="text-sm font-medium capitalize">{user.provider}</p>
            </div>
          </div>

          {/* Stats */}
          {user.stats && (
            <div className="border-t border-border-light dark:border-border-dark pt-4 mb-6">
              <p className="text-sm font-medium mb-3">Activity Stats</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold">{user.stats.totalSlips}</p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Slips</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-success">{user.stats.wins}</p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Wins</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-error">{user.stats.losses}</p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Losses</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{user.stats.winRate}%</p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Win Rate</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={onAdjustGT}
              icon={<Coins size={16} />}
              className="flex-1"
              size="sm"
            >
              Adjust GT
            </Button>
            {user.isBanned ? (
              <Button
                variant="outline"
                onClick={onUnban}
                icon={<UserCheck size={16} />}
                className="flex-1 !text-success !border-success"
                size="sm"
              >
                Unban
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={onBan}
                icon={<UserX size={16} />}
                className="flex-1 !text-error !border-error"
                size="sm"
              >
                Ban
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};
